// --- START PATCH: Cart provider with auth-aware and route-aware refresh logic, plus optimistic methods ---
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import axios, { AUTH_EVENTS } from "@/auth/axiosWithInterceptor";
import { CartContext, CartContextType } from "./CartContext";

type CartAPIItem = {
  quantity: number;
};

type CartAPIResponse = {
  items: CartAPIItem[];
};

// Routes that should trigger a cart refresh when navigated to
const CART_REFRESH_ROUTES = ["/cart", "/checkout"];

// Check if path matches any cart refresh route (exact match at start)
function isCartRefreshRoute(pathname: string): boolean {
  return CART_REFRESH_ROUTES.some(route => 
    pathname === route || pathname.startsWith(route + "/")
  );
}

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [count, setCount] = useState(0);
  const location = useLocation();
  
  // Track last refresh to avoid duplicate refreshes
  const lastRefreshRef = useRef<number>(0);
  const MIN_REFRESH_INTERVAL_MS = 1000; // Minimum 1 second between refreshes
  
  // Track if we were previously on a cart refresh route to avoid re-refresh on sub-route navigation
  const wasOnCartRouteRef = useRef<boolean>(false);

  const refresh: CartContextType["refresh"] = useCallback(async () => {
    // Debounce: skip if we just refreshed
    const now = Date.now();
    if (now - lastRefreshRef.current < MIN_REFRESH_INTERVAL_MS) {
      return;
    }
    lastRefreshRef.current = now;

    try {
      const res = await axios.get<CartAPIResponse>("/cart", { withCredentials: true });
      const items = Array.isArray(res.data?.items) ? res.data.items : [];
      const totalCount = items.reduce((acc: number, it: CartAPIItem) => acc + (it.quantity ?? 0), 0);
      setCount(totalCount);
    } catch {
      setCount(0);
    }
  }, []);

  // --- START: Optimistic increment for immediate badge update after add-to-cart ---
  const increment: CartContextType["increment"] = useCallback((delta: number) => {
    setCount((prev) => Math.max(0, prev + delta));
  }, []);
  // --- END: Optimistic increment ---

  // --- START: Optimistic reset for immediate badge update after order placement ---
  const reset: CartContextType["reset"] = useCallback(() => {
    setCount(0);
  }, []);
  // --- END: Optimistic reset ---

  // --- START: Initial refresh on mount ---
  useEffect(() => {
    refresh();
  }, [refresh]);
  // --- END: Initial refresh on mount ---

  // --- START: Auth-state event listeners (login/logout and token refresh) ---
  useEffect(() => {
    const handleAuthRefreshed = () => {
      refresh();
    };

    const handleAuthLoggedIn = () => {
      refresh();
    };

    const handleAuthLoggedOut = () => {
      refresh();
    };

    // Subscribe to auth events
    window.addEventListener(AUTH_EVENTS.REFRESHED, handleAuthRefreshed);
    window.addEventListener(AUTH_EVENTS.LOGGED_IN, handleAuthLoggedIn);
    window.addEventListener(AUTH_EVENTS.LOGGED_OUT, handleAuthLoggedOut);

    return () => {
      window.removeEventListener(AUTH_EVENTS.REFRESHED, handleAuthRefreshed);
      window.removeEventListener(AUTH_EVENTS.LOGGED_IN, handleAuthLoggedIn);
      window.removeEventListener(AUTH_EVENTS.LOGGED_OUT, handleAuthLoggedOut);
    };
  }, [refresh]);
  // --- END: Auth-state event listeners ---

  // --- START: Route change listeners for /cart and /checkout ---
  // Only refresh when entering cart/checkout routes from outside, not on sub-route navigation
  useEffect(() => {
    const isOnCartRoute = isCartRefreshRoute(location.pathname);
    const wasOnCartRoute = wasOnCartRouteRef.current;
    
    // Refresh only when entering cart/checkout routes from outside
    if (isOnCartRoute && !wasOnCartRoute) {
      refresh();
    }
    
    // Update tracking ref
    wasOnCartRouteRef.current = isOnCartRoute;
  }, [location.pathname, refresh]);
  // --- END: Route change listeners ---

  return (
    <CartContext.Provider value={{ count, refresh, increment, reset }}>
      {children}
    </CartContext.Provider>
  );
};
// --- END PATCH: Cart provider with auth-aware and route-aware refresh logic, plus optimistic methods ---
